<template>
    <div class="pricing-analytics">
        <a-spin :spinning="loading">
            <div v-if="!loading && data && data.pricing_analytics">
                <div class="toolbar">
                    <div class="buttons">
                        <IconButton
                            :classNames="viewMode === 'chart' ? 'selected' : ''"
                            :onClick="() => (viewMode = 'chart')"
                            v-tippy="{ content: 'Grafik Görünümü' }"
                        >
                            <md-icon>insights</md-icon>
                        </IconButton>
                        <IconButton
                            :classNames="viewMode === 'pie' ? 'selected' : ''"
                            :onClick="() => (viewMode = 'pie')"
                            v-tippy="{ content: 'Pasta Grafik' }"
                        >
                            <md-icon>donut_small</md-icon>
                        </IconButton>
                        <IconButton
                            :classNames="viewMode === 'table' ? 'selected' : ''"
                            :onClick="() => (viewMode = 'table')"
                            v-tippy="{ content: 'Tablo Görünümü' }"
                        >
                            <md-icon>table_view</md-icon>
                        </IconButton>
                        <IconButton :onClick="exportToExcel" v-tippy="{ content: 'Excel İndir' }">
                            <md-icon>download</md-icon>
                        </IconButton>
                    </div>
                </div>

                <div class="stats-row">
                    <StatCard title="Toplam Hacim" :value="totalVolume" unit="mesaj" />
                    <StatCard title="Toplam Maliyet" :value="totalCost.toFixed(2)" unit="$" />
                    <StatCard title="Ortalama Birim Maliyet" :value="avgCostPerMessage.toFixed(4)" unit="$/mesaj" />
                </div>

                <div class="pricing-breakdown">
                    <h3>Fiyatlandırma Kategorisi Dağılımı</h3>
                    <div class="pricing-cards">
                        <div v-for="(value, category) in pricingCategoryBreakdown" :key="category" class="pricing-card">
                            <div class="pricing-name">{{ getPricingCategoryLabel(category) }}</div>
                            <div class="pricing-volume">{{ value.volume }} mesaj</div>
                            <div class="pricing-cost">${{ value.cost.toFixed(2) }}</div>
                        </div>
                    </div>
                </div>

                <div class="pricing-type-breakdown">
                    <h3>Fiyatlandırma Tipi Dağılımı</h3>
                    <div class="pricing-cards">
                        <div v-for="(value, type) in pricingTypeBreakdown" :key="type" class="pricing-card">
                            <div class="pricing-name">{{ getPricingTypeLabel(type) }}</div>
                            <div class="pricing-volume">{{ value.volume }} mesaj</div>
                            <div class="pricing-cost">${{ value.cost.toFixed(2) }}</div>
                        </div>
                    </div>
                </div>

                <div class="chart-container" v-if="viewMode === 'chart'">
                    <StackedAreaChart
                        title="Fiyatlandırma İstatistikleri"
                        :x-axis-data="getXAxisData()"
                        :legends="getPricingLegends()"
                        :colors="pricingColors"
                        :series="getPricingSeries()"
                    />
                </div>

                <div class="pie-container" v-if="viewMode === 'pie'">
                    <PieChartV2 title="Maliyet Dağılımı" :legends="getPricingLegends()" :series="getPieSeries()" />
                </div>

                <div class="table-container" v-if="viewMode === 'table'">
                    <a-table :columns="columns" :data-source="tableData" :pagination="{ pageSize: 10 }" size="middle" />
                </div>
            </div>

            <div v-else-if="!loading" class="empty-state">
                <a-empty description="Veri bulunamadı. Lütfen tarih aralığını kontrol edin." />
            </div>
        </a-spin>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import _ from "lodash";
import { expertToExcel } from "../../../../../utils/utils";
import StackedAreaChart from "../../../Chatbot/pages/reports/charts/StackedAreaChart.vue";
import PieChartV2 from "../../../Chatbot/pages/reports/charts/PieChartV2.vue";
import StatCard from "../../../Chatbot/pages/reports/charts/StatCard.vue";
import IconButton from "../../../Settings/components/IconButton.vue";

export default Vue.extend({
    name: "PricingAnalytics",
    components: {
        StackedAreaChart,
        PieChartV2,
        StatCard,
        IconButton,
    },
    props: {
        channel: {
            type: Object,
            required: true,
        },
        filters: {
            type: Object,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            viewMode: "chart",
            pricingColors: ["#80FFA5", "#00DDFF", "#37A2FF", "#FF0087", "#FFBF00", "#C070FF"],
            pricingCategoryLabels: {
                AUTHENTICATION: "Kimlik Doğrulama",
                AUTHENTICATION_INTERNATIONAL: "Uluslararası Kimlik Doğrulama",
                MARKETING: "Pazarlama",
                SERVICE: "Hizmet",
                UTILITY: "Yardımcı",
                REFERRAL_CONVERSION: "Referans Dönüşümü",
            },
            pricingTypeLabels: {
                REGULAR: "Normal",
                FREE_CUSTOMER_SERVICE: "Ücretsiz Müşteri Hizmeti",
                FREE_ENTRY_POINT: "Ücretsiz Giriş Noktası",
            },
            columns: [
                {
                    title: "Tarih",
                    dataIndex: "date",
                    key: "date",
                },
                {
                    title: "Ülke",
                    dataIndex: "country",
                    key: "country",
                },
                {
                    title: "Kategori",
                    dataIndex: "category",
                    key: "category",
                },
                {
                    title: "Tip",
                    dataIndex: "type",
                    key: "type",
                },
                {
                    title: "Hacim",
                    dataIndex: "volume",
                    key: "volume",
                    sorter: (a, b) => a.volume - b.volume,
                },
                {
                    title: "Maliyet",
                    dataIndex: "cost",
                    key: "cost",
                    sorter: (a, b) => parseFloat(a.costRaw) - parseFloat(b.costRaw),
                },
                {
                    title: "Tier",
                    dataIndex: "tier",
                    key: "tier",
                },
            ],
        };
    },
    computed: {
        dataPoints() {
            const data = _.get(this.data, "pricing_analytics.data", []) || [];
            if (data.length > 0) {
                return _.get(data, "0.data_points", []) || [];
            }
            return [];
        },
        totalVolume() {
            return this.dataPoints.reduce((sum, dp) => sum + (dp.volume || 0), 0);
        },
        totalCost() {
            return this.dataPoints.reduce((sum, dp) => sum + (dp.cost || 0), 0);
        },
        avgCostPerMessage() {
            if (this.totalVolume === 0) return 0;
            return this.totalCost / this.totalVolume;
        },
        pricingCategoryBreakdown() {
            const breakdown = {};
            this.dataPoints.forEach((dp) => {
                const category = dp.pricing_category || "OTHER";
                if (!breakdown[category]) {
                    breakdown[category] = { volume: 0, cost: 0 };
                }
                breakdown[category].volume += dp.volume || 0;
                breakdown[category].cost += dp.cost || 0;
            });
            return breakdown;
        },
        pricingTypeBreakdown() {
            const breakdown = {};
            this.dataPoints.forEach((dp) => {
                const type = dp.pricing_type || "OTHER";
                if (!breakdown[type]) {
                    breakdown[type] = { volume: 0, cost: 0 };
                }
                breakdown[type].volume += dp.volume || 0;
                breakdown[type].cost += dp.cost || 0;
            });
            return breakdown;
        },
        tableData() {
            return this.dataPoints.map((dp, index) => ({
                key: index,
                date: moment.unix(dp.start).format("DD/MM/YYYY"),
                country: dp.country || "-",
                category: this.getPricingCategoryLabel(dp.pricing_category),
                type: this.getPricingTypeLabel(dp.pricing_type),
                volume: dp.volume || 0,
                cost: "$" + (dp.cost || 0).toFixed(2),
                costRaw: dp.cost || 0,
                tier: dp.tier || "-",
            }));
        },
    },
    methods: {
        getPricingCategoryLabel(category) {
            return this.pricingCategoryLabels[category] || category || "Diğer";
        },
        getPricingTypeLabel(type) {
            return this.pricingTypeLabels[type] || type || "Diğer";
        },
        getPricingLegends() {
            return Object.keys(this.pricingCategoryBreakdown).map((cat) => this.getPricingCategoryLabel(cat));
        },
        getXAxisData() {
            const dates = _.uniq(this.dataPoints.map((dp) => moment.unix(dp.start).format("DD/MM")));
            return dates;
        },
        getPricingSeries() {
            const categories = Object.keys(this.pricingCategoryBreakdown);
            const dates = _.uniq(this.dataPoints.map((dp) => moment.unix(dp.start).format("DD/MM")));

            return categories.map((category, idx) => {
                const data = dates.map((date) => {
                    const points = this.dataPoints.filter(
                        (dp) => moment.unix(dp.start).format("DD/MM") === date && dp.pricing_category === category
                    );
                    return points.reduce((sum, p) => sum + (p.cost || 0), 0);
                });

                return {
                    name: this.getPricingCategoryLabel(category),
                    type: "line",
                    stack: "Total",
                    smooth: true,
                    lineStyle: { width: 0 },
                    showSymbol: false,
                    areaStyle: { opacity: 0.8 },
                    emphasis: { focus: "series" },
                    data: data,
                };
            });
        },
        getPieSeries() {
            return [
                {
                    name: "Maliyet Dağılımı",
                    type: "pie",
                    radius: ["40%", "70%"],
                    avoidLabelOverlap: false,
                    label: {
                        show: true,
                        formatter: "{b}: ${c} ({d}%)",
                    },
                    data: Object.entries(this.pricingCategoryBreakdown).map(([category, value]) => ({
                        name: this.getPricingCategoryLabel(category),
                        value: parseFloat(value.cost.toFixed(2)),
                    })),
                },
            ];
        },
        exportToExcel() {
            const exportData = this.tableData.map((row) => ({
                Tarih: row.date,
                Ülke: row.country,
                Kategori: row.category,
                Tip: row.type,
                Hacim: row.volume,
                Maliyet: row.cost,
                Tier: row.tier,
            }));
            expertToExcel(exportData, "pricing_analytics.xlsx", "Pricing Analytics");
        },
    },
});
</script>

<style scoped lang="scss">
.pricing-analytics {
    padding: 20px;

    .toolbar {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 20px;

        .buttons {
            display: flex;
            border: 1px solid var(--color-border-default);
            border-radius: 4px;

            .icon-button {
                padding: 8px;
                display: flex;
                justify-content: center;
                align-items: center;
                border-right: 1px solid var(--color-border-default);
                border-radius: unset !important;

                &.selected {
                    background-color: var(--color-bg-info-low-hover);
                }

                &:last-child {
                    border-right: none;
                }

                &:hover {
                    background-color: var(--color-bg-neutral-pressed);
                }
            }
        }
    }

    .stats-row {
        display: flex;
        gap: 20px;
        margin-bottom: 30px;
        flex-wrap: wrap;

        .stat-card {
            flex: 1;
            min-width: 200px;
        }
    }

    .pricing-breakdown,
    .pricing-type-breakdown {
        margin-bottom: 30px;

        h3 {
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 15px;
        }

        .pricing-cards {
            display: flex;
            gap: 15px;
            flex-wrap: wrap;

            .pricing-card {
                padding: 15px 20px;
                background: var(--color-bg-subtle);
                border-radius: 8px;
                min-width: 150px;

                .pricing-name {
                    font-weight: 600;
                    margin-bottom: 8px;
                    color: var(--color-text-strong-default);
                    font-size: 13px;
                }

                .pricing-volume {
                    font-size: 18px;
                    font-weight: 700;
                    color: var(--color-text-info);
                }

                .pricing-cost {
                    font-size: 14px;
                    color: var(--color-text-default);
                    margin-top: 4px;
                }
            }
        }
    }

    .chart-container,
    .pie-container {
        min-height: 400px;
        margin-bottom: 30px;
    }

    .table-container {
        margin-bottom: 30px;
    }

    .empty-state {
        padding: 60px 20px;
        text-align: center;
    }
}
</style>
